/**
 * « Suppression en cours »: whether an ephemeral environment is being deleted,
 * for every screen that lists environments — the environments card, the home
 * page, the sidebar, the branch page and its switcher.
 *
 * HOW A DELETION UNFOLDS. Deleting an ephemeral branch only touches git
 * (`branchRoutes.ts`). The ApplicationSet `prj-<p>-ephemeral` notices within
 * its 180 s requeue and deletes the wrapper Application
 * `appenv-prj-<p>-<b>`, whose finalizer deletes what it manages — among which
 * the environment's Application `prj-<p>-<b>`, the one these screens list
 * (label `waadoo.ovh/project`; the wrapper carries none). That one is in turn held
 * by its own finalizer while its resources go, then disappears.
 *
 * THE SIGNALS. An environment is « being deleted » when its branch is
 * ephemeral and any of these holds:
 *  (a) its Application carries `metadata.deletionTimestamp` — the end of the
 *      sequence, read from ArgoCD at no extra cost;
 *  (b) its branch is no longer in git while the Application still exists —
 *      the minutes before the ApplicationSet acts. Read from `waadoo-projects`
 *      (`GET /projects/:p/branches/ephemeral`, members included);
 *  (c) this browser tab has just deleted it (`pendingDeletions.ts`) — so
 *      the state shows before anything is read again.
 *
 * FAIL SOFT. (b) needs a list that was read, complete, and AFTER the
 * Application was created — a list older than the branch says nothing about
 * it. Unknown, failed, not yet loaded or cut: nothing is marked. The route
 * exists on ludo only (`waadooProjects.gitlab.*`); elsewhere (b) is never
 * known, and (a) and (c) remain.
 */

import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { isEphemeralBranch } from './branchKind';
import { fetchEphemeralBranches } from './branchManagement';
import {
  dropPendingDeletionsSeenInGit,
  isPendingDeletion,
  type PendingDeletion,
} from './pendingDeletions';
import {
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from './sharedLoad';
import { useVisiblePolling } from './visiblePolling';

/** How often a screen showing a deletion reads again. */
export const DELETION_POLL_MS = 15_000;

/**
 * How old a shared answer of the BROWSER (`sharedLoad.ts`) a poll accepts:
 * under the poll period, so every tick asks the backend again past the
 * browser's 30 s share, yet two screens ticking a few seconds apart (sidebar
 * and home page) share one request. What the backend answers may itself be
 * up to its own TTL old: ArgoCD is read through the proxy uncached, but the
 * git list comes from a GitLab read the backend keeps `BRANCH_READ_TTL_MS`
 * (30 s, `branchRoutes.ts`) — a branch deleted by someone else can take that
 * long more to show « being deleted ».
 */
export const DELETION_POLL_MAX_AGE_MS = 10_000;

/**
 * The margin by which an Application must predate the git list for (b).
 * `creationTimestamp` is cut to the second, and the backend's clock and the
 * API server's are two clocks; the ApplicationSet's own delay (up to 180 s
 * between a branch and its Application) dwarfs it.
 */
export const GIT_LIST_MARGIN_MS = 5_000;

/**
 * How long one project's git list is shared between screens and pages — the
 * backend keeps its GitLab read as long (`BRANCH_READ_TTL_MS`). A poll asks
 * for an answer younger than {@link DELETION_POLL_MAX_AGE_MS}, a Refresh for
 * a new one.
 */
export const GIT_LIST_TTL_MS = 30_000;

/** The ephemeral branches of a project in git, as (b) reads them. */
export interface EphemeralBranchList {
  branches: ReadonlySet<string>;
  /** Epoch milliseconds, backend clock: when GitLab was read. */
  readAt: number;
  /**
   * Epoch milliseconds, BROWSER clock: when this browser asked for the list
   * — the clock (c)'s marks are written with (`pendingDeletions.ts`).
   */
  requestedAt: number;
}

/** What the rule reads of an environment (`ProjectEnvironment`, `BranchOption`). */
export interface DeletionCandidate {
  branch: string;
  /** `metadata.creationTimestamp` of the Application. */
  createdAt?: string;
  /** `metadata.deletionTimestamp` of the Application. */
  deletionTimestamp?: string;
}

export interface DeletionContext {
  project: string;
  /** The project's git list; undefined while unknown. */
  git: EphemeralBranchList | undefined;
  pending: readonly PendingDeletion[];
  now: number;
}

/** Signal (b): see the header for why a list must postdate the Application. */
function goneFromGit(
  env: DeletionCandidate,
  git: EphemeralBranchList | undefined,
): boolean {
  if (!git || git.branches.has(env.branch) || !env.createdAt) {
    return false;
  }
  const created = Date.parse(env.createdAt);
  return !Number.isNaN(created) && created + GIT_LIST_MARGIN_MS < git.readAt;
}

export function isBranchDeleting(
  env: DeletionCandidate,
  context: DeletionContext,
): boolean {
  if (!isEphemeralBranch(env.branch)) {
    return false;
  }
  return (
    Boolean(env.deletionTimestamp) ||
    isPendingDeletion(
      context.pending,
      context.project,
      env.branch,
      context.now,
    ) ||
    goneFromGit(env, context.git)
  );
}

/** The branches of `envs` being deleted. */
export function deletingBranches(
  envs: readonly DeletionCandidate[],
  context: DeletionContext,
): ReadonlySet<string> {
  return new Set(
    envs.filter(env => isBranchDeleting(env, context)).map(env => env.branch),
  );
}

/**
 * The route's answer as (b) reads it; undefined for an answer that cannot
 * prove a branch gone — cut by GitLab's page, or with no usable date.
 */
export function toEphemeralBranchList(
  answer: {
    branches: string[];
    readAt: string;
    complete: boolean;
  },
  requestedAt: number,
): EphemeralBranchList | undefined {
  const readAt = Date.parse(answer.readAt);
  if (!answer.complete || Number.isNaN(readAt)) {
    return undefined;
  }
  return { branches: new Set(answer.branches), readAt, requestedAt };
}

const sharedLists = new Map<
  string,
  SharedLoad<EphemeralBranchList | undefined>
>();

function sharedListOf(
  project: string,
): SharedLoad<EphemeralBranchList | undefined> {
  let shared = sharedLists.get(project);
  if (!shared) {
    shared = createSharedLoad(GIT_LIST_TTL_MS);
    sharedLists.set(project, shared);
  }
  return shared;
}

/** The next read of `project`'s git list asks the backend again. */
export function forgetEphemeralBranchList(project: string): void {
  sharedLists.delete(project);
}

/**
 * The git lists of `projects`. Read again when `pollKey` changes (a « being
 * deleted » poll: an answer younger than {@link DELETION_POLL_MAX_AGE_MS})
 * and when `refreshKey` changes (the Refresh button: a new answer).
 * A project is in the map only once its list has been read, complete; a
 * failed read takes it out. One read per project shared between the screens
 * for {@link GIT_LIST_TTL_MS} — the `readAt` it carries keeps a shared answer
 * honest (see {@link isBranchDeleting}).
 *
 * Callers pass only the projects showing an ephemeral environment: the
 * others have nothing (b) could mark, and GitLab is not asked for them.
 */
export function useEphemeralBranchLists(
  projects: readonly string[],
  pollKey: number,
  refreshKey: number = 0,
): ReadonlyMap<string, EphemeralBranchList> {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // A string, so a new array of the same projects does not read again.
  const key = [...new Set(projects)].sort().join('\n');
  const [lists, setLists] = useState<{
    key: string;
    lists: ReadonlyMap<string, EphemeralBranchList>;
  }>({ key: '', lists: new Map() });
  // The keys of the previous run, to tell a poll from a Refresh from a mount
  // or a change of projects (neither: the shared answer as it is).
  const lastPoll = useRef(pollKey);
  const lastRefresh = useRef(refreshKey);

  useEffect(() => {
    const wanted = key === '' ? [] : key.split('\n');
    let active = true;
    let loadOptions: SharedLoadOptions = {};
    if (refreshKey !== lastRefresh.current) {
      loadOptions = { force: true };
    } else if (pollKey !== lastPoll.current) {
      loadOptions = { maxAgeMs: DELETION_POLL_MAX_AGE_MS };
    }
    lastRefresh.current = refreshKey;
    lastPoll.current = pollKey;
    // The lists of the projects no longer asked go at once.
    setLists(current => ({
      key,
      lists: new Map(
        [...current.lists].filter(([project]) => wanted.includes(project)),
      ),
    }));

    const store = (project: string, list: EphemeralBranchList | undefined) =>
      setLists(current => {
        const next = new Map(current.lists);
        if (list) {
          next.set(project, list);
        } else {
          next.delete(project);
        }
        return { key: current.key, lists: next };
      });

    const load = async (project: string) => {
      try {
        const list = await sharedListOf(project).load(async () => {
          const requestedAt = Date.now();
          return toEphemeralBranchList(
            await fetchEphemeralBranches({ fetchApi, discoveryApi, project }),
            requestedAt,
          );
        }, loadOptions);
        // A branch this tab deleted, yet in a list asked for AFTER the
        // deletion: it has been pushed again, and (c) must not keep calling
        // it « being deleted » — its Application may never be removed, and
        // nothing else would clear the mark before its TTL. Done even for an
        // unmounted caller: it is a fact about the stored marks.
        if (list) {
          dropPendingDeletionsSeenInGit(
            project,
            list.branches,
            list.requestedAt,
          );
        }
        if (active) {
          store(project, list);
        }
      } catch {
        // Unknown: nothing is marked on a failed read.
        if (active) {
          store(project, undefined);
        }
      }
    };
    // `load` turns every failure into state, so nothing can reject.
    wanted.forEach(project => void load(project));
    return () => {
      active = false;
    };
  }, [key, pollKey, refreshKey, fetchApi, discoveryApi]);

  // Only the lists of the projects asked NOW: the render between a change of
  // `projects` and the effect above must not read the previous ones.
  return lists.key === key ? lists.lists : new Map();
}

/**
 * Calls `poll` every {@link DELETION_POLL_MS} while `active`, and stops as
 * soon as it is not — when nothing shown is being deleted any more. Paused
 * while the tab is hidden, with a read as soon as it is shown again
 * (`useVisiblePolling`); a poll returning its promise is not called again
 * before it settles.
 */
export function useDeletionPolling(
  active: boolean,
  poll: () => void | Promise<unknown>,
  intervalMs: number = DELETION_POLL_MS,
): void {
  useVisiblePolling(poll, intervalMs, active);
}
